import { useEffect, useRef, useState } from 'react';
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const capabilities = [
  {
    title: 'Trained Conversation Flow',
    description:
      'Your genie follows a proven conversation script designed for your use case. It asks the right questions, provides relevant answers, and guides visitors toward the outcome you want — naturally and consistently.',
    image: '/home-page-imagery/replicate-prediction-ta1cdk9s49rme0ctv04b1caef8.webp',
    genieIcon: 'chat-check' as const,
  },
  {
    title: 'Multi-Channel Deployment',
    description:
      'Deploy across voice calls, web chat, SMS, and phone — all from a single configuration. Your genie meets customers wherever they are, with a consistent experience across every channel.',
    image: '/home-page-imagery/replicate-prediction-3ymns27nhsrm80ctv0ptrjgqmw.webp',
    genieIcon: 'share' as const,
  },
  {
    title: 'Built-In Knowledge Base',
    description:
      'Upload your documents, FAQs, and product information. Your genie draws on your actual business knowledge to answer questions accurately — no scripting every response by hand.',
    image: '/home-page-imagery/tmp9o88qb_2.webp',
    genieIcon: 'brain' as const,
  },
  {
    title: 'Conversation Intelligence',
    description:
      'Every interaction generates insights. See what customers ask most, track sentiment in real time, and discover patterns that help you improve your products, services, and messaging.',
    image: '/home-page-imagery/replicate-prediction-amq310djhsrma0ctv08bfgqahg.webp',
    genieIcon: 'trending-up' as const,
  },
];

const CapabilityCard = ({
  capability,
  index,
}: {
  capability: (typeof capabilities)[0];
  index: number;
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isImageLeft = index % 2 === 0;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -30px 0px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`flex flex-col ${isImageLeft ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-6 sm:gap-8 lg:gap-12 xl:gap-16`}
    >
      {/* Image */}
      <div
        className={`w-full lg:w-1/2 transition-all duration-700 ease-out ${
          isVisible
            ? 'opacity-100 translate-y-0 lg:translate-x-0'
            : `opacity-0 translate-y-8 lg:translate-y-0 ${isImageLeft ? 'lg:-translate-x-16' : 'lg:translate-x-16'}`
        }`}
      >
        <div className="relative overflow-hidden rounded-xl sm:rounded-2xl shadow-lg group">
          <img
            src={capability.image}
            alt={capability.title}
            loading="lazy"
            className="w-full h-48 sm:h-64 md:h-80 lg:h-96 object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#4E9CFF]/20 via-[#FF4DB8]/10 to-[#4E9CFF]/20 rounded-xl sm:rounded-2xl" />
        </div>
      </div>

      {/* Text */}
      <div
        className={`w-full lg:w-1/2 ${isImageLeft ? 'lg:pl-4' : 'lg:pr-4'} transition-all duration-700 ease-out delay-150 text-center lg:text-left ${
          isVisible
            ? 'opacity-100 translate-y-0 lg:translate-x-0'
            : `opacity-0 translate-y-8 lg:translate-y-0 ${isImageLeft ? 'lg:translate-x-16' : 'lg:-translate-x-16'}`
        }`}
      >
        <div className="flex items-center gap-3 mb-3 sm:mb-4 justify-center lg:justify-start">
          <div className="h-1 w-12 sm:w-16 bg-gradient-to-r from-primary to-secondary rounded-full" />
          <GenieIcon name={capability.genieIcon} size={24} color="#4E9CFF" />
        </div>
        <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 sm:mb-4">
          {capability.title}
        </h3>
        <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 leading-relaxed max-w-md mx-auto lg:mx-0 lg:max-w-none">
          {capability.description}
        </p>
      </div>
    </div>
  );
};

export const PlaybookCapabilities = () => {
  return (
    <section className="w-full bg-white py-12 sm:py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12 md:mb-16 lg:mb-20">
          <div className="h-1 w-16 bg-gradient-to-r from-primary to-secondary rounded-full mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold px-2 pb-2 leading-tight text-gray-900">
            What you get
          </h2>
          <p className="mt-3 text-sm sm:text-base lg:text-lg text-gray-600 max-w-2xl mx-auto">
            Every playbook comes with everything you need to deploy a production-ready voice genie.
          </p>
        </div>

        <div className="space-y-10 sm:space-y-12 md:space-y-16 lg:space-y-20">
          {capabilities.map((capability, index) => (
            <CapabilityCard key={capability.title} capability={capability} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};
